import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import StructuredData, { organizationSchema, websiteSchema } from "../components/StructuredData";

const HERO = "/images/hero-30piece.png";
const FACE = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/62cf71d58a5f0c214ab721902c716539195509a24bb7497c1fdbd12384a6a3fc.png";
const EYE = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/c87b6059eab5e3784918fcec1d99655e8f207fe475d3cd9dfa53b927003d1cc0.png";
const POWDER = "https://static.prod-images.emergentagent.com/jobs/b676cfa9-b99a-467a-b748-e01e0fba16e9/images/5c97f9724bfdaad482043e8f51261955fa7fece6ffe9ee3b63347e7b3c7de85d.png";

export default function Home({ onOpenExpert }) {
  return (
    <div data-testid="home-page">
      <SEO
        title="BrushBasics — The Guide to Every Makeup Brush"
        description="An editorial guide to 30 makeup brushes. What each does, how to use it, and which one belongs in your kit. No trends. No noise. Just considered technique."
        path="/"
      />
      <StructuredData schemas={[organizationSchema, websiteSchema]} />
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7 bb-fade-up">
              <div className="overline mb-6">Volume 01 · The Brush Edit</div>
              <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
                Every brush has a purpose.
                <br />
                <em className="font-light italic text-stone-700">Here&apos;s how to use it.</em>
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-stone-600 font-light">
                BrushBasics is a quiet, considered guide to the makeup brushes living in your drawer. Learn what each one
                does, the techniques that make it sing, and how to care for it — without the noise.
              </p>
              <div className="mt-6">
                <Link
                  to="/brush/powder-brush"
                  data-testid="home-free-sample-chip"
                  className="inline-flex items-center gap-2 bg-[#E8D5D1] hover:bg-[#dec4be] text-stone-900 px-4 py-2 text-xs tracking-wide transition-colors"
                >
                  <span className="overline" style={{ color: "#2C2A29" }}>Free Sample</span>
                  <span className="text-sm">Try a brush tutorial →</span>
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/catalog"
                  data-testid="hero-cta-catalog"
                  className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
                >
                  Browse the Catalog
                </Link>
                <Link
                  to="/quiz"
                  data-testid="hero-cta-quiz"
                  className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-7 py-4 transition-colors"
                >
                  Take Brush Finder Quiz
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative overflow-hidden">
                <img
                  src={HERO}
                  alt="Editorial flat lay of a 30-piece professional makeup brush set on soft beige marble"
                  className="w-full h-[420px] sm:h-[520px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden lg:block bg-white border border-[#E5E0D8] p-5 max-w-[220px]">
                <div className="overline mb-2">Brushes Profiled</div>
                <div className="font-serif-display text-4xl text-stone-900">30</div>
                <p className="text-xs text-stone-500 mt-1">curated, illustrated, and dissected.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bb-divider" />
      </section>

      {/* Three columns */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {[
            { n: "01", t: "Identify", d: "Know exactly which brush you're holding — and what it was made to do." },
            { n: "02", t: "Apply", d: "Step-by-step techniques that turn product on a brush into product on the face." },
            { n: "03", t: "Care", d: "Wash, reshape, and store so your brushes last for years, not weeks." },
          ].map((c) => (
            <div key={c.n} className="border-t border-[#E5E0D8] pt-6">
              <div className="overline mb-4">{c.n}</div>
              <h3 className="font-serif-display text-3xl text-stone-900 mb-3">{c.t}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-[#F2EFEB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-4">The Categories</div>
              <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light">A brush for every part of the face.</h2>
            </div>
            <Link
              data-testid="featured-cta-catalog"
              to="/catalog"
              className="hidden md:inline-block text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]"
            >
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { cat: "face", title: "Face", count: 10, img: FACE },
              { cat: "eyes", title: "Eyes", count: 14, img: EYE },
              { cat: "lips", title: "Cheeks & Lips", count: 6, img: POWDER },
            ].map((c) => (
              <Link
                key={c.cat}
                to={`/catalog?category=${c.cat}`}
                data-testid={`featured-cat-${c.cat}`}
                className="group block bg-white border border-[#E5E0D8] bb-card-hover"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-6 flex items-center justify-between">
                  <div>
                    <div className="overline mb-2">Category</div>
                    <div className="font-serif-display text-2xl text-stone-900">{c.title}</div>
                  </div>
                  <span className="text-sm text-stone-500">{c.count} brushes</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Free guide banner */}
      <section className="bg-[#2C2A29] text-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <div className="overline mb-2" style={{ color: "#E8D5D1" }}>Free · 32 Pages · No Sign-up Catch</div>
            <h3 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
              The complete pocket guide,
              <em className="italic"> on us.</em>
            </h3>
          </div>
          <Link
            to="/free-guide"
            data-testid="home-free-guide-cta"
            className="text-xs uppercase tracking-[0.22em] bg-[#F9F7F3] text-stone-900 hover:bg-white px-7 py-4 transition-colors"
          >
            Get the Free Guide →
          </Link>
        </div>
      </section>

      {/* Expert callout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="overline mb-4">The Brush Expert</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light leading-tight">
              Stuck on a look?
              <br />
              <em className="italic text-stone-700">Ask anything.</em>
            </h2>
            <p className="mt-6 text-base text-stone-600 leading-relaxed max-w-xl">
              A pocket makeup artist for those late-night &quot;wait, which brush do I use for that?&quot; questions.
              Trained on technique, not trends.
            </p>
            <button
              data-testid="home-open-expert"
              onClick={onOpenExpert}
              className="mt-8 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
            >
              Open the Atelier
            </button>
          </div>
          <div className="md:col-span-5">
            <img src={POWDER} alt="Macro shot of soft beige powder texture" className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
